import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  ImageList,
  ImageListItem,
  Divider,
  useTheme,
  Avatar,
  TablePagination,
  Rating,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Header from "../components/Header";
import BreadcrumbsBar from "../components/BreadcrumbsBar";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../theme";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ReportPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [reports]);
  // Fetch reports on mount
  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/cmt/maintenance-reports`);

      setReports(res.data.data || []);
    } catch (err) {
      // Handle error
      setReports([]);
    }
    setLoading(false);
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setOpenDialog(true);
  };

  return (
    <Box className="main-container mt-4">
      <BreadcrumbsBar />

      <Paper sx={{ p: 3, mt: 3, height: "75vh" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer
              sx={{
                maxHeight: "65vh",
                overflow: "auto", // enable both horizontal and vertical
                minWidth: "100%", // ensure it spans available space
                scrollbarWidth: "thin",
                boxShadow: 3,
                "&::-webkit-scrollbar": {
                  height: "8px", // 👈 ensures horizontal bar shows
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f0f0f0",
                  borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#c1c1c1",
                  borderRadius: "8px",
                  border: "2px solid #f0f0f0",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#a0a0a0",
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <b>{t("report_page.col_unit")}</b>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="center">
                        {t("report_page.col_tenant")}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="center">
                        {t("report_page.col_provider")}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <b>{t("report_page.col_service")}</b>
                    </TableCell>
                    <TableCell>
                      <b>{t("report_page.col_status")}</b>
                    </TableCell>
                    <TableCell>
                      <b>{t("report_page.col_completed_at")}</b>
                    </TableCell>
                    <TableCell>
                      <b>{t("report_page.col_actions")}</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {t("report_page.no_reports")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    reports
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="notranslate">
                            {r.unit?.name || "-"}
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              className="notranslate"
                              title={
                                <Box>
                                  <Typography>
                                    {r.tenant?.user.email}
                                  </Typography>
                                  <Typography dir="ltr">
                                    {r.tenant?.user.phone}
                                  </Typography>
                                </Box>
                              }
                            >
                              <Box
                                justifyItems={"center"}
                                className="notranslate"
                              >
                                <Avatar src={r.tenant?.user.avatar_url}>
                                  {r.tenant?.user?.username[0] || "-"}
                                </Avatar>
                                <Typography>
                                  {r.tenant?.user.username}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              className="notranslate"
                              title={
                                <Box className="notranslate">
                                  <Typography>
                                    {r.serviceprovider?.user.email}
                                  </Typography>
                                  <Typography dir="ltr">
                                    {r.serviceprovider?.user.phone}
                                  </Typography>
                                </Box>
                              }
                            >
                              <Box
                                justifyItems={"center"}
                                className="notranslate"
                              >
                                <Avatar
                                  src={r.serviceprovider?.user.avatar_url}
                                >
                                  {r.serviceprovider?.user?.username[0] || "-"}
                                </Avatar>
                                <Typography>
                                  {r.serviceprovider?.user.username}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={r.compoundServiceType?.name || "-"}
                              color="info"
                            ></Chip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={r.status}
                              color={
                                r.status === "COMPLETED" ? "success" : "info"
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {r.updatedAt
                              ? new Date(r.updatedAt).toLocaleString()
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={t("report_page.view_report")}>
                              <IconButton onClick={() => handleViewReport(r)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={reports.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>

      {/* Report Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: colors.primary[600],
            color: "#fff",
            py: 2,
            px: 3,
            mb: -1, // to bring content closer
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            {t("report_page.maintenance_report")}
          </Typography>
          <IconButton
            onClick={() => setOpenDialog(false)} // <-- Your close handler
            edge="end"
            size="large"
            sx={{
              color: "#888",
              "&:hover": { color: "error.main", bgcolor: "transparent" },
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              p: { xs: 2, sm: 4 },
              background: colors.primary[700],
              borderRadius: 3,
              boxShadow: 3,
              maxWidth: 700,
              mx: "auto",
              my: 2,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              mb={2}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Box>
                <Typography variant="h6" color="text.secondary">
                  {t("report_page.unit")}
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={500}
                  className="notranslate"
                >
                  {selectedReport?.unit?.name || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" color="text.secondary">
                  {t("report_page.building")}
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={500}
                  className="notranslate"
                >
                  {selectedReport?.unit?.building?.name || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" color="text.secondary">
                  {t("report_page.service_type")}
                </Typography>
                <Typography variant="h5" fontWeight={500}>
                  {selectedReport?.compoundServiceType?.name || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" color="text.secondary">
                  {t("report_page.completed_at")}
                </Typography>
                <Typography variant="h5">
                  {selectedReport?.updatedAt
                    ? new Date(selectedReport?.updatedAt).toLocaleString()
                    : "-"}
                </Typography>
              </Box>
            </Stack>

            {/* Section: Maintenance Request */}
            <Box
              sx={{
                background: colors.primary[800],
                borderRadius: 2,
                p: 2.5,
                mb: 2,
                border: "1px solid #e4e9f2",
              }}
            >
              <Typography variant="h6" color="primary" mb={1}>
                {t("report_page.request_description")}
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {selectedReport?.description || "-"}
              </Typography>
            </Box>

            {/* Section: Completion Report */}
            <Box
              sx={{
                background: colors.primary[800],
                borderRadius: 2,
                p: 2.5,
                mb: 2,
                border: "1px solid #e4e9f2",
              }}
            >
              <Typography variant="h6" color={colors.greenAccent[100]} mb={1}>
                {t("report_page.completion_report")}
              </Typography>
              <Typography variant="h5">
                {selectedReport?.report || "-"}
              </Typography>
            </Box>

            <Box
              sx={{
                background: colors.primary[800],
                borderRadius: 2,
                p: 2.5,
                mb: 2,
                border: "1px solid #e4e9f2",
              }}
            >
              <Typography variant="h6" color={colors.greenAccent[100]} mb={1}>
                {t("report_page.review")}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <Rating
                  value={selectedReport?.rating ?? 0}
                  precision={0.5}
                  readOnly
                  size="large"
                  sx={{ mt: 0.5 }}
                />
                <Typography variant="body2">
                  {selectedReport?.rating}
                </Typography>
              </Stack>
              <Typography variant="body2">
                {selectedReport?.ratingComment}
              </Typography>
            </Box>

            {/* Section: Photos */}
            {selectedReport?.photos && selectedReport?.photos.length > 0 && (
              <Box
                sx={{
                  background: colors.primary[800],
                  borderRadius: 2,
                  p: 2.5,
                  mb: 1,
                  border: "1px solid #e4e9f2",
                }}
              >
                <Typography variant="h6" color={colors.blueAccent[200]} mb={2}>
                  {t("report_page.photos")}
                </Typography>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  {selectedReport.photos.map((img, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 150,
                        height: 120,
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: 2,
                        background: "#e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #e4e9f2",
                      }}
                    >
                      <img
                        src={img}
                        alt={`Report Photo ${i + 1}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReportPage;
